import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Observable } from 'rxjs';

import { ShipmentDeliveryLineBatchInfoDetailDto, ShipmentDeliveryLineBatchInfoDto } from '@shared/src/dtos/shipment/delivery/ShipmentDeliveryLineBatchInfoDto';
import { ShipmentDeliveryTotalFieldTypes } from '@shared/src/enums/ShipmentDeliveryTotalFieldTypes';
import { DeliveryLineColumns } from './DeliveryLineColumns';
import { IDeliveryLine } from './IDeliveryLine';

/***
 * Base compartida per als dos editors de línies d'albarà:
 *
 *  - shared-shipment-delivery-component: l'albarà "teòric" del shipment
 *  - updatedeliverylines-dialog (CMR): la realitat executada a la càrrega
 *
 * En realitat gestionen la mateixa dada (un és còpia de l'altre), i abans
 * tenien la lògica duplicada. Aquí hi viu tot el que és comú: la còpia de
 * treball, la visibilitat de columnes, expandir/plegar, alta/baixa de
 * línies, els lots de fabricació i el control de canvis.
 *
 * El que canvia per cada costat (com es desa i quins camps existeixen) es
 * resol als mètodes abstractes i a supportedColumns().
 *
 * NOTA: els dos costats treballen amb buffer + confirmar. La còpia de
 * treball es fa amb JSON clone (com ja feia el diàleg del CMR), de manera
 * que cancel·lar simplement descarta la còpia.
 */
@Component({
    template: ''
})
export abstract class SharedDeliveryLinesBaseComponent<T extends IDeliveryLine> {

    @Input() public readonly: boolean = false;

    @Output() public dirtyChanged: EventEmitter<boolean> = new EventEmitter();
    @Output() public saved: EventEmitter<any> = new EventEmitter();

    /*** Còpia de treball: mai s'edita l'original fins que no es confirma */
    public lines: T[] = [];
    public columns: DeliveryLineColumns = new DeliveryLineColumns();

    /***
     * Quins camps suporta AQUEST editor (independent de si alguna línia hi té
     * dada). Es fa servir pel desplegable de detall: aquest ha de mostrar
     * sempre totes les propietats que l'editor sap pintar (amb "---" si la
     * línia no en té valor), no només les que ja tenen dada a alguna línia.
     */
    public allColumns: DeliveryLineColumns = new DeliveryLineColumns();

    public expandedLine: T = null;
    public deleteSelected: T = null;

    private _dirty: boolean = false;
    public get dirty(): boolean {
        return this._dirty;
    }

    private _editMode: boolean = false;
    @Input() public set editMode(value: boolean) {
        if (this._editMode === value)
            return;
        this._editMode = value;
        this.refreshColumns();
    }
    public get editMode(): boolean {
        return this._editMode;
    }

    /***
     * Camps que aquest editor concret sap pintar. Es passa tal qual a
     * DeliveryLineColumns.compute(): un camp que NO hi sigui queda amagat
     * SEMPRE (edició i visualització), sense error — veure el comentari
     * gran a dalt de DeliveryLineColumns.ts abans d'afegir un camp nou a
     * IDeliveryLine i oblidar-te'l aquí.
     */
    protected abstract supportedColumns(): string[];

    /*** Desa la còpia de treball. La subclasse decideix contra quin endpoint. */
    protected abstract persist(lines: T[]): Observable<any>;

    /*** Crea una línia buida del tipus concret */
    protected abstract buildEmptyLine(): T;

    /*** Carrega les línies d'origen fent-ne una còpia de treball */
    public loadLines(source: T[]) {
        this.lines = source != null ? JSON.parse(JSON.stringify(source)) : [];
        this.setDirty(false);
        this.refreshColumns();
    }

    public refreshColumns() {
        this.columns = DeliveryLineColumns.compute(this.lines, this.editMode, this.supportedColumns());
        // editMode=true força que compute() només miri "és un camp suportat?", ignorant si hi ha dada
        this.allColumns = DeliveryLineColumns.compute(this.lines, true, this.supportedColumns());
    }

    protected setDirty(value: boolean) {
        if (this._dirty === value)
            return;
        this._dirty = value;
        this.dirtyChanged.next(value);
    }

    /*** Qualsevol edició d'un camp d'una línia passa per aquí */
    public onLineValueChanged() {
        if (this.readonly)
            return;
        this.setDirty(true);
    }

    public onExpandedLine(line: T) {
        this.expandedLine = this.expandedLine === line ? null : line;
    }

    public isExpanded(line: T): boolean {
        return this.expandedLine === line;
    }

    public onAddLine() {
        if (this.readonly)
            return;
        let line = this.buildEmptyLine();
        this.lines.push(line);
        this.expandedLine = line;
        this.setDirty(true);
        this.refreshColumns();
    }

    public onDeleteSelectedEvent(line: T) {
        this.deleteSelected = line;
    }

    public showDeleteButton(line: T): boolean {
        return !this.deleteSelected || this.deleteSelected !== line;
    }

    public onDeleteLine(line: T) {
        if (this.readonly)
            return;
        let index = this.lines.indexOf(line);
        if (index < 0)
            return;
        this.lines.splice(index, 1);
        this.deleteSelected = null;
        if (this.expandedLine === line)
            this.expandedLine = null;
        this.setDirty(true);
        this.refreshColumns();
    }

    // ///////////////////////////////////////// Lots de fabricació

    public getBatchDetails(line: T): ShipmentDeliveryLineBatchInfoDetailDto[] {
        return ShipmentDeliveryLineBatchInfoDto.getDetails(line != null ? line.batchInfo : null);
    }

    public onAddBatch(line: T) {
        if (this.readonly || line == null)
            return;
        if (line.batchInfo == null)
            line.batchInfo = ShipmentDeliveryLineBatchInfoDto.buildEmpty();
        if (line.batchInfo.details == null)
            line.batchInfo.details = [];
        line.batchInfo.details.push(ShipmentDeliveryLineBatchInfoDetailDto.buildEmpty());
        this.setDirty(true);
    }

    public onDeleteBatch(line: T, detail: ShipmentDeliveryLineBatchInfoDetailDto) {
        if (this.readonly || line == null || line.batchInfo == null || line.batchInfo.details == null)
            return;
        let index = line.batchInfo.details.indexOf(detail);
        if (index < 0)
            return;
        line.batchInfo.details.splice(index, 1);
        this.setDirty(true);
    }

    public onBatchValueChanged() {
        if (this.readonly)
            return;
        this.setDirty(true);
    }

    public batchCount(line: T): number {
        return ShipmentDeliveryLineBatchInfoDto.countBatches(line != null ? line.batchInfo : null);
    }

    public batchTotalBoxQuantity(line: T): number {
        return ShipmentDeliveryLineBatchInfoDto.totalBoxQuantity(line != null ? line.batchInfo : null);
    }

    public batchTotalNetWeight(line: T): number {
        return ShipmentDeliveryLineBatchInfoDto.totalNetWeightInKg(line != null ? line.batchInfo : null);
    }

    // ///////////////////////////////////////// Totals del peu

    public totalQuantity(): number {
        return this.lines.reduce((sum, l) => sum + (l.quantity ? +l.quantity : 0), 0);
    }

    public totalKg(): number {
        return this.lines.reduce((sum, l) => sum + (l.kg ? +l.kg : 0), 0);
    }

    public totalNetKg(): number {
        return this.lines.reduce((sum, l) => sum + (l.netKg ? +l.netKg : 0), 0);
    }

    public totalBoxes(): number {
        return this.lines.reduce((sum, l) => sum + (l.boxes ? +l.boxes : 0), 0);
    }

    public totalBags(): number {
        return this.lines.reduce((sum, l) => sum + (l.bags ? +l.bags : 0), 0);
    }

    public totalSacks(): number {
        return this.lines.reduce((sum, l) => sum + (l.sacks ? +l.sacks : 0), 0);
    }

    public totalLitres(): number {
        return this.lines.reduce((sum, l) => sum + (l.litres ? +l.litres : 0), 0);
    }

    public totalM3(): number {
        return this.lines.reduce((sum, l) => sum + (l.m3 ? +l.m3 : 0), 0);
    }

    public totalPalletCount(): number {
        return this.lines.reduce((sum, l) => sum + (l.palletCount ? +l.palletCount : 0), 0);
    }

    public totalPrice(): number {
        return this.lines.reduce((sum, l) => sum + (l.totalPrice ? +l.totalPrice : 0), 0);
    }

    public totalLines(): number {
        return this.lines.length;
    }

    /*** Valor calculat sumant la còpia de treball de les línies (independent de qualsevol fixat) */
    public getComputedTotal(field: ShipmentDeliveryTotalFieldTypes): number {
        switch (field) {
            case ShipmentDeliveryTotalFieldTypes.quantity: return this.totalQuantity();
            case ShipmentDeliveryTotalFieldTypes.price: return this.totalPrice();
            case ShipmentDeliveryTotalFieldTypes.kg: return this.totalKg();
            case ShipmentDeliveryTotalFieldTypes.m3: return this.totalM3();
            case ShipmentDeliveryTotalFieldTypes.pallet: return this.totalPalletCount();
            case ShipmentDeliveryTotalFieldTypes.netKg: return this.totalNetKg();
            case ShipmentDeliveryTotalFieldTypes.boxes: return this.totalBoxes();
            case ShipmentDeliveryTotalFieldTypes.bags: return this.totalBags();
            case ShipmentDeliveryTotalFieldTypes.sacks: return this.totalSacks();
            case ShipmentDeliveryTotalFieldTypes.litres: return this.totalLitres();
        }
    }

    // ///////////////////////////////////////// Totals fixats manualment
    //
    // Comú als dos costats: un total es pot "fixar" perquè no segueixi la suma
    // de línies. És sessió-local (res es desa fins que es confirma, mateix
    // "dirty" i mateix botó de Guardar que les línies) — la subclasse decideix
    // com envia els valors fixats a persist() i com inicialitza l'estat de
    // partida amb initTotalsState() (l'albarà del shipment ja sap si un total
    // estava fixat al servidor; el diàleg del CMR sempre comença sense tocar
    // res, veure el seu propi comentari).

    public readonly totalFieldTypes = ShipmentDeliveryTotalFieldTypes;

    private touchedTotalFields: { [key: string]: boolean } = {};
    private fixedTotalValues: { [key: string]: number } = {};

    protected initTotalsState(initialTouched: { [key: string]: boolean }, initialValues: { [key: string]: number }) {
        this.touchedTotalFields = { ...initialTouched };
        this.fixedTotalValues = { ...initialValues };
    }

    public getIsFixed(field: ShipmentDeliveryTotalFieldTypes): boolean {
        return !!this.touchedTotalFields[field];
    }

    public getFixedValue(field: ShipmentDeliveryTotalFieldTypes): number {
        return this.fixedTotalValues[field];
    }

    /*** Al marcar "fixar" sense cap valor previ, partim del calculat perquè l'usuari ajusti a partir d'aquí */
    public onToggleFixed(field: ShipmentDeliveryTotalFieldTypes, isFixed: boolean) {
        if (this.readonly)
            return;
        this.touchedTotalFields[field] = isFixed;
        if (isFixed && (this.fixedTotalValues[field] == null))
            this.fixedTotalValues[field] = this.getComputedTotal(field);
        this.setDirty(true);
    }

    public onFixedValueChanged(field: ShipmentDeliveryTotalFieldTypes, value: string) {
        if (this.readonly)
            return;
        this.touchedTotalFields[field] = true;
        this.fixedTotalValues[field] = value != null && value !== '' ? +value : 0;
        this.setDirty(true);
    }

    /*** El que cal enviar al servidor en confirmar: el fixat si n'hi ha, si no la suma en viu */
    protected getTotalValueToSend(field: ShipmentDeliveryTotalFieldTypes): number {
        return this.getIsFixed(field) ? this.getFixedValue(field) : this.getComputedTotal(field);
    }

    // ///////////////////////////////////////// Confirmar / cancel·lar

    public onConfirm() {
        if (this.readonly || !this.dirty)
            return;
        this.persist(this.lines).subscribe(result => {
            this.setDirty(false);
            this.saved.next(result);
        });
    }

    public onCancel() {
        this.setDirty(false);
        this.expandedLine = null;
        this.deleteSelected = null;
        this.reloadAfterCancel();
    }

    /*** La subclasse torna a carregar des de l'origen per descartar la còpia */
    protected abstract reloadAfterCancel(): void;
}
